import { Link2, CheckCircle2, Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * 히어로 옆 미리보기 카드.
 *
 * 제품이 어떤 화면인지 보여 주는 정지 그림이다. 실제 데이터를 쓰지 않으므로
 * 숫자가 실적처럼 읽히지 않도록 예시임을 분명히 한다(aria-label).
 */

const CLICK_BARS = [
  { height: '38%', tone: 'bg-primary-100 dark:bg-primary-900/60' },
  { height: '52%', tone: 'bg-primary-100 dark:bg-primary-900/60' },
  { height: '45%', tone: 'bg-primary-100 dark:bg-primary-900/60' },
  { height: '70%', tone: 'bg-primary-200 dark:bg-primary-800/70' },
  { height: '62%', tone: 'bg-primary-200 dark:bg-primary-800/70' },
  { height: '88%', tone: 'bg-primary-400' },
  { height: '100%', tone: 'bg-primary-600' },
]

export default function HeroDemoCard() {
  const { t } = useTranslation()

  const countries = [
    { key: 'demoCountryKr', share: 64, color: '#2563eb' },
    { key: 'demoCountryUs', share: 21, color: '#60a5fa' },
    { key: 'demoCountryJp', share: 9, color: '#93c5fd' },
  ]

  return (
    <div
      aria-label={t('home.featuresTitle')}
      className="flex flex-col gap-4 rounded-[20px] border border-gray-200 bg-white p-5 shadow-[0_30px_60px_-30px_rgba(30,64,175,0.35),0_1px_3px_rgba(15,23,42,0.06)] dark:border-gray-700 dark:bg-gray-800"
    >
      {/* 입력 줄 */}
      <div className="flex gap-2">
        <div className="flex h-12 min-w-0 flex-1 items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3.5 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
          <Link2 className="h-[18px] w-[18px] shrink-0" />
          <span className="truncate">{t('home.demoUrlExample')}</span>
        </div>
        <span className="grid h-12 shrink-0 place-items-center rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white">
          {t('home.demoShorten')}
        </span>
      </div>

      {/* 결과 줄 */}
      <div className="flex items-center gap-3 rounded-xl border border-primary-100 bg-primary-50 p-3.5 dark:border-primary-900 dark:bg-primary-950/40">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-white text-primary-600 dark:bg-gray-800">
          <CheckCircle2 className="h-[18px] w-[18px]" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-base font-bold text-primary-900 dark:text-primary-200">
            shorty.kr/sp26
          </span>
          <span className="truncate text-xs text-gray-500 dark:text-gray-400">
            {t('home.demoResultMeta')}
          </span>
        </div>
        <span className="hidden shrink-0 items-center gap-1.5 rounded-lg border border-primary-100 bg-white px-3 py-1.5 text-[13px] font-semibold text-primary-700 dark:border-primary-900 dark:bg-gray-800 dark:text-primary-300 sm:inline-flex">
          <Copy className="h-3.5 w-3.5" />
          {t('home.demoCopy')}
        </span>
      </div>

      {/* 지표 */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-3.5 rounded-xl border border-gray-100 p-4 dark:border-gray-700">
          <div className="flex items-baseline justify-between">
            <span className="text-[13px] text-gray-500 dark:text-gray-400">
              {t('home.demoClicksLabel')}
            </span>
            <span className="text-xs font-semibold text-green-600">+18%</span>
          </div>
          <span className="text-[28px] font-extrabold leading-none tracking-tight text-gray-900 dark:text-white">
            2,481
          </span>
          <div className="flex h-16 items-end gap-1.5">
            {CLICK_BARS.map((bar, index) => (
              <span
                key={index}
                className={`flex-1 rounded ${bar.tone}`}
                style={{ height: bar.height }}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-3 rounded-xl border border-gray-100 p-4 dark:border-gray-700">
          <span className="text-[13px] text-gray-500 dark:text-gray-400">
            {t('home.demoCountriesLabel')}
          </span>
          <div className="flex flex-col gap-2.5 text-[13px]">
            {countries.map(country => (
              <div key={country.key} className="flex flex-col gap-1">
                <div className="flex justify-between text-gray-900 dark:text-gray-200">
                  <span>{t(`home.${country.key}`)}</span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {country.share}%
                  </span>
                </div>
                <span className="h-1 overflow-hidden rounded-sm bg-gray-100 dark:bg-gray-700">
                  <span
                    className="block h-full rounded-sm"
                    style={{
                      width: `${country.share}%`,
                      background: country.color,
                    }}
                  />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
